import { useState } from 'react';
import { User } from '../../types/user.response';
import { usersApi } from '../../services/api/users.api';
import SearchableDropdown from '../../components/shared/SearchableDropdown';

interface AccountSettingsProps {
    user: User;
    refreshAuth: () => Promise<void>;
}

const yearOptions = [2023, 2024, 2025, 2026].map(year => ({
    _id: year.toString(),
    name: year.toString(),
}));

export default function AccountSettings({ user, refreshAuth }: AccountSettingsProps) {
    const [entryYear, setEntryYear] = useState(user.entryYear);
    const [yearSaving, setYearSaving] = useState(false);
    const [yearSuccess, setYearSuccess] = useState(false);
    const [yearError, setYearError] = useState<string | null>(null);

    const handleYearSave = async () => {
        if (entryYear === user.entryYear) return;
        try {
            setYearSaving(true);
            setYearError(null);
            await usersApi.updateProfile({ entryYear });
            await refreshAuth();
            setYearSuccess(true);
            setTimeout(() => setYearSuccess(false), 2000);
        } catch (err) {
            setYearError(err instanceof Error ? err.message : 'Failed to update entry year');
        } finally {
            setYearSaving(false);
        }
    };

    const yearChanged = entryYear !== user.entryYear;

    return (
        <div className="space-y-10">
            {/* Entry Year Section */}
            <section>
                <h2 className="text-base font-medium text-gray-900 mb-1">Entry Year</h2>
                <p className="text-sm text-gray-500 mb-4">The year you entered Cornell.</p>

                <div className="max-w-xs">
                    <SearchableDropdown
                        value={entryYear}
                        options={yearOptions}
                        onChange={setEntryYear}
                        excludeIds={[]}
                        placeholder="Select year"
                        noResultsText="No years found"
                    />
                </div>

                <div className="mt-8 flex items-center gap-3">
                    <button
                        onClick={handleYearSave}
                        disabled={!yearChanged || yearSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {yearSaving ? 'Saving...' : 'Save'}
                    </button>
                    {yearSuccess && <span className="text-sm text-green-600">Saved successfully</span>}
                    {yearError && <span className="text-sm text-red-600">{yearError}</span>}
                </div>
            </section>
        </div>
    );
}
